import {
  isNumericFilterField,
  METRICS,
  type MetricType,
  type NumericOperatorType,
  type SelectedFilterOption,
} from 'features/charts-v2/types'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import type {
  Breakdown,
  ChartPanel,
} from 'features/charts-v2/types/filter-hash/v2'
import type {
  Breakdown as YogiQLBreakdown,
  ChartRequest,
  Condition,
  FieldCatalogResponse,
  FieldCondition,
  FieldSpec,
  MetricCondition,
  Operator,
  PlainMetric,
  Scalar,
} from 'features/charts-v2/types/yogiql'
import {
  YOGIQL_CHART_SPECS,
  type YogiQLChartName,
  type YogiQLChartSpec,
  type YogiQLSlot,
} from 'features/charts-v2/types/yogiql-chart-specs'
import { augmentMetricsForWeighting } from 'features/charts-v2/utils/augment-metrics-for-weighting'
import { withYogiQLVersion } from 'features/charts-v2/utils/yogiql-version'
import { ALIAS_FOR_NULL_VALUES } from 'src/constants/filter-values'

/** Theme cluster uuids and dataset project uuids resolved to the names the
 *  YogiQL takes; the Container builds this from the filter options. */
export type YogiQLNames = {
  themes: Record<string, string>
  datasets: Record<string, string>
  /** The hub's brands split by ownership, for the drill-down's bucket expansion. */
  brandOwnership?: { mine: string[]; competitor: string[] }
  /** Parent theme name to its member theme names (`Parent: Child`). */
  themeParents?: Record<string, string[]>
}

export const EMPTY_YOGIQL_NAMES: YogiQLNames = { themes: {}, datasets: {} }

// Not MAX_RENDERABLE_DATAPOINTS: the YogiQL limit counts wire rows, the page's
// cap counts rendered rows (a line of 80 themes by month is ~1040 wire rows).
export const YOGIQL_ROW_CEILING = 50_000

/** Either the request, or why this panel must take the legacy route. */
export type YogiQLEncoding =
  | { ok: true; request: ChartRequest }
  | { ok: false; reason: string }

export const CHART_BY_TYPE: Partial<Record<ChartTypeV2, YogiQLChartName>> = {
  [ChartTypeV2.BAR_HORIZONTAL]: 'bar',
  [ChartTypeV2.LINE]: 'line',
  [ChartTypeV2.BAR_HORIZONTAL_STACKED]: 'stacked_bar',
  [ChartTypeV2.BUBBLE]: 'bubble',
  [ChartTypeV2.SCATTER]: 'scatter',
  [ChartTypeV2.PIE]: 'pie',
  [ChartTypeV2.HEATMAP]: 'heatmap',
  [ChartTypeV2.TREEMAP]: 'treemap',
  [ChartTypeV2.TABLE]: 'table',
  [ChartTypeV2.RIDGELINE]: 'ridgeline',
  [ChartTypeV2.SANKEY]: 'sankey',
}

// The breakdown roles a panel fills, in PANEL order. Heatmap is why this is not
// the spec's order: the renderer reads category[0] as the x axis, the spec lists y first.
export const PANEL_BREAKDOWN_ROLES: Record<string, string[]> = {
  bar: ['Breakdown'],
  line: ['Series'],
  scatter: ['Breakdown'],
  bubble: ['Breakdown'],
  stacked_bar: ['Bars', 'Segments'],
  pie: ['Slices'],
  heatmap: ['X axis', 'Y axis'],
  treemap: ['Boxes'],
  table: ['Rows'],
  ridgeline: ['Series'],
  sankey: ['Columns'],
}

/** The slot each position of a `count`-long list fills: required slots always
 *  fill, optional ones take the surplus left to right. Mirrors the compiler. */
export function placeSlots(slots: YogiQLSlot[], count: number): YogiQLSlot[] {
  let surplus = Math.max(count - slots.reduce((n, s) => n + s.min, 0), 0)
  const placed: YogiQLSlot[] = []
  for (const slot of slots) {
    const extra = Math.min(surplus, slot.max - slot.min)
    surplus -= extra
    for (let i = 0; i < slot.min + extra; i++) placed.push(slot)
  }
  return placed
}

/** The request positions of `count` breakdowns in panel order, time (line) last. */
export function panelBreakdownOrder(
  spec: YogiQLChartSpec,
  count: number,
): number[] {
  const roles = placeSlots(spec.breakdowns, count).map((s) => s.role)
  const panel = PANEL_BREAKDOWN_ROLES[spec.name] ?? []
  const rank = (i: number): number => {
    const at = panel.indexOf(roles[i] ?? '')
    return at === -1 ? panel.length : at
  }
  return [...Array(count).keys()].sort((a, b) => rank(a) - rank(b))
}

export const YOGIQL_METRIC: Record<MetricType, PlainMetric> = {
  [METRICS.RECORD_COUNT.id]: { fn: 'count', field: 'record' },
  [METRICS.SENTENCE_COUNT.id]: { fn: 'count', field: 'sentence' },
  [METRICS.SENTIMENT.id]: { fn: 'avg', field: 'sentiment' },
  [METRICS.RATING.id]: { fn: 'avg', field: 'rating' },
  [METRICS.NET_PROMOTER_SCORE.id]: { fn: 'avg', field: 'rating' },
}

export const toYogiQLMetric = (metric: MetricType): PlainMetric =>
  YOGIQL_METRIC[metric]

export const metricKey = (metric: PlainMetric): string =>
  `${metric.fn}(${metric.field})`

export const breakdownKey = (breakdown: YogiQLBreakdown): string =>
  breakdown.fn ? `${breakdown.field}.${breakdown.fn}` : breakdown.field

// A ridgeline panel names its score; the request states what that implied: the
// count at the score's grain, the bins, and for rating the unrated excluded
// (the compiler guards a sentiment bin, not a star). Rating or NPS, else sentiment.
type RidgelineScore = {
  metrics: PlainMetric[]
  bins: YogiQLBreakdown
  filters: Condition[]
}
const RIDGELINE_SENTIMENT: RidgelineScore = {
  metrics: [
    { fn: 'count', field: 'sentence' },
    { fn: 'avg', field: 'sentiment' },
  ],
  bins: { field: 'sentiment', fn: 'bin' },
  filters: [],
}
export const RIDGELINE_RATING: RidgelineScore = {
  metrics: [
    { fn: 'count', field: 'record' },
    { fn: 'avg', field: 'rating' },
  ],
  bins: { field: 'rating', fn: 'star' },
  filters: [{ field: 'rating', op: 'is_not_null' }],
}

function ridgelineScore(panel: ChartPanel): RidgelineScore {
  const first = panel.metrics[0]
  const rating = first !== undefined && toYogiQLMetric(first).field === 'rating'
  return rating ? RIDGELINE_RATING : RIDGELINE_SENTIMENT
}

// A sankey ribbon is as wide as the panel's count (sentences when it names
// none) and colored by sentiment, as the legacy query fetched it.
function sankeyMetrics(panel: ChartPanel): PlainMetric[] {
  const first = panel.metrics[0]
  const count =
    first !== undefined && toYogiQLMetric(first).fn === 'count'
      ? toYogiQLMetric(first)
      : toYogiQLMetric(METRICS.SENTENCE_COUNT.id)
  return [count, toYogiQLMetric(METRICS.SENTIMENT.id)]
}

// The group field a grouped breakdown resolves to, which a sankey draws
// before its members.
export const GROUP_FIELDS = new Set([
  'parent_theme',
  'brand_group',
  'product_group',
])

export const STANDARD_BREAKDOWN: Record<string, YogiQLBreakdown> = {
  brand: { field: 'brand' },
  product: { field: 'product' },
  theme: { field: 'theme' },
  source_type: { field: 'source_type' },
  country: { field: 'country' },
  sentiment: { field: 'sentiment', fn: 'polarity' },
  rating: { field: 'rating', fn: 'star' },
  dataset: { field: 'dataset' },
  promotional: { field: 'promotion' },
}

/** The YogiQL breakdown for a panel breakdown, or why it cannot be one. */
export function toYogiQLBreakdown(
  breakdown: Breakdown,
  fields: FieldSpec[],
): YogiQLBreakdown | string {
  if (breakdown.kind === 'custom') {
    return fields.some((f) => f.custom && f.name === breakdown.field)
      ? { field: breakdown.field }
      : `custom breakdown '${breakdown.field}' is not in the hub's field catalog`
  }
  if ('grouped' in breakdown && breakdown.grouped) {
    if (breakdown.id === 'theme') return { field: 'parent_theme' }
    // The group is absent from the catalog on a hub with no owned list.
    const group = `${breakdown.id}_group`
    return {
      field: fields.some((f) => f.name === group) ? group : breakdown.id,
    }
  }
  return (
    STANDARD_BREAKDOWN[breakdown.id] ??
    `breakdown '${breakdown.id}' has no YogiQL field`
  )
}

// Only these two mean null in a filter value (06 §31); `None`, `unknown`, `''`
// and the rest of `isNullValueAlias` are matched literally, as the transpiler does.
const isNull = (value: string): boolean =>
  value === 'Null' || value === ALIAS_FOR_NULL_VALUES

const splitNulls = (
  values: string[],
): { plain: string[]; hasNull: boolean } => {
  const plain = values.filter((v) => !isNull(v))
  return { plain, hasNull: plain.length !== values.length }
}

// `is` → in, `is not` → not_in; a null alias widens `in` and narrows `not_in`,
// as the legacy IS / IS_NOT emitters do. `missing` ids are members too: a
// condition may hold only them.
function membership(
  field: string,
  operator: SelectedFilterOption['operator'],
  values: string[],
  fn?: FieldCondition['fn'],
  missing: string[] = [],
): Condition[] | string {
  const { plain, hasNull } = splitNulls(values)
  const bucket = fn ? { fn } : {}
  const members = {
    ...(plain.length || !missing.length ? { values: plain } : {}),
    ...(missing.length ? { missing } : {}),
  }
  const hasMembers = plain.length > 0 || missing.length > 0
  if (operator === 'is') {
    if (hasMembers) {
      return [{ field, ...bucket, op: 'in', ...members, include_null: hasNull }]
    }
    return hasNull ? [{ field, op: 'is_null' }] : []
  }
  if (operator === 'is not') {
    const notIn: FieldCondition = {
      field,
      ...bucket,
      op: 'not_in',
      ...members,
    }
    if (hasMembers) {
      return hasNull
        ? [{ and: [notIn, { field, op: 'is_not_null' }] }]
        : [notIn]
    }
    return hasNull ? [{ field, op: 'is_not_null' }] : []
  }
  return `operator '${operator}' on '${field}' has no YogiQL encoding`
}

export const NUMERIC_OP: Record<NumericOperatorType, MetricCondition['op']> = {
  'greater than': 'gt',
  'greater than or equal to': 'gte',
  'less than': 'lt',
  'less than or equal to': 'lte',
  'equal to': 'eq',
  'not equal to': 'eq',
}

export const NUMERIC_METRIC: Record<string, PlainMetric> = {
  record_count: { fn: 'count', field: 'record' },
  sentence_count: { fn: 'count', field: 'sentence' },
  sentiment_filter: { fn: 'avg', field: 'sentiment' },
}

// A stored hash spells an absent bound `null`, so `!= null`, never `!== undefined`.
function range(
  field: string,
  min: number | null | undefined,
  max: number | null | undefined,
  includeNull: boolean,
): FieldCondition | null {
  const nullable = includeNull ? { include_null: true } : {}
  if (min != null && max != null) {
    return { field, op: 'between', values: [min, max], ...nullable }
  }
  if (min != null) return { field, op: 'gte', value: min, ...nullable }
  if (max != null) return { field, op: 'lte', value: max, ...nullable }
  return null
}

export const STANDARD_FILTER_FIELD: Record<string, string> = {
  brand: 'brand',
  product_hierarchy: 'product',
  product: 'item',
  product_name: 'item',
  source_type: 'source_type',
  source_name: 'source_type',
  source: 'source',
  source_title: 'source',
  country: 'country',
  post_country: 'country',
  parent_theme: 'parent_theme',
}

const POLARITY = new Set(['positive', 'neutral', 'negative'])

function sentimentFilter(filter: SelectedFilterOption): Condition[] | string {
  const field =
    filter.field === 'post_sentiment' ? 'post_sentiment' : 'sentiment'
  const { plain, hasNull } = splitNulls(filter.values)
  const lowered = plain.map((v) => v.toLowerCase())
  if (lowered.some((v) => !POLARITY.has(v))) {
    return `sentiment value '${plain.join(', ')}' is not a polarity`
  }
  const nullAware = hasNull ? [...lowered, 'Null'] : lowered
  const conditions = membership(field, filter.operator, nullAware, 'polarity')
  if (typeof conditions === 'string') return conditions
  // Stored ranges become inclusive `between` (G19).
  const scoreRange = range(field, filter.min, filter.max, false)
  return scoreRange ? [...conditions, scoreRange] : conditions
}

function ratingFilter(filter: SelectedFilterOption): Condition[] | string {
  const { plain, hasNull } = splitNulls(filter.values)
  const stars = plain.filter((v) => !Number.isNaN(Number(v)))
  if (filter.operator === 'is not') {
    // Legacy NOTs the reviews-only toggle and a range along with the stars.
    const scoped = filter.valueType === 1 || filter.valueType === 2
    const ranged = !stars.length && (filter.min != null || filter.max != null)
    if (scoped || ranged) {
      return `rating 'is not' with a reviews/ratings-only toggle or a range stays legacy (legacy negates them with the stars)`
    }
    const values = hasNull ? [...stars, 'Null'] : stars
    return membership('rating', 'is not', values, 'star')
  }
  if (filter.operator !== 'is') {
    return `operator '${filter.operator}' on '${filter.field}' has no YogiQL encoding`
  }
  const ratingOnly: FieldCondition | null =
    filter.valueType === 1
      ? { field: 'rating_only', op: 'eq', value: false }
      : filter.valueType === 2
        ? { field: 'rating_only', op: 'eq', value: true }
        : null
  const score: FieldCondition | null = stars.length
    ? {
        field: 'rating',
        fn: 'star',
        op: 'in',
        values: stars,
        include_null: hasNull,
      }
    : range('rating', filter.min, filter.max, hasNull)
  if (score) return ratingOnly ? [score, ratingOnly] : [score]
  if (!hasNull) return ratingOnly ? [ratingOnly] : []
  const nullOnly: FieldCondition = { field: 'rating', op: 'is_null' }
  return ratingOnly ? [{ or: [nullOnly, ratingOnly] }] : [nullOnly]
}

// TODO(PROD-6108): drop once no hub's filter-options cache serves the old
// `Promotional, <type>` labels; until then options and saved filters carry it.
export const PROMOTION_PREFIX = 'Promotional, '
// The panel's label for a null promotion; `Null` / `No Value` match nothing there.
export const NOT_PROMOTIONAL = 'Not Promotional'

// 06 §29: `Not Promotional` is null, and `is not` keeps the null rows unless it
// names them, as the legacy emitter does.
function promotionFilter(filter: SelectedFilterOption): Condition[] | string {
  const field = 'promotion'
  if (filter.operator !== 'is' && filter.operator !== 'is not') {
    return `operator '${filter.operator}' on '${field}' has no YogiQL encoding`
  }
  const plain = filter.values
    .filter((v) => v !== NOT_PROMOTIONAL)
    .map((v) =>
      v.startsWith(PROMOTION_PREFIX) ? v.slice(PROMOTION_PREFIX.length) : v,
    )
  const hasNull = plain.length !== filter.values.length
  const isIn = filter.operator === 'is'
  if (!plain.length) {
    return hasNull ? [{ field, op: isIn ? 'is_null' : 'is_not_null' }] : []
  }
  return [
    {
      field,
      op: isIn ? 'in' : 'not_in',
      values: plain,
      include_null: isIn ? hasNull : !hasNull,
    },
  ]
}

// Ask Yogi's stored "my brands vs competitors" filter is YogiQL's own
// `brand_group`, offered only on a hub that owns brands.
const BRAND_GROUP_FIELD = 'brand_group'
const BRAND_GROUP_VALUES = new Set(['My Brands', 'Competitor Brands'])
// Closed bucket vocabularies; `parent_theme` is open, so it is absent.
const CLOSED_GROUP_VALUES: Record<string, Set<string>> = {
  [BRAND_GROUP_FIELD]: BRAND_GROUP_VALUES,
  product_group: new Set(['My Products', 'Competitor Products']),
}

function brandGroupFilter(
  filter: SelectedFilterOption,
  fields: FieldSpec[],
): Condition[] | string {
  if (!fields.some((f) => f.name === BRAND_GROUP_FIELD)) {
    return `filter field '${BRAND_GROUP_FIELD}' needs owned brands, which this hub has none of`
  }
  const unknown = filter.values.find((v) => !BRAND_GROUP_VALUES.has(v))
  if (unknown !== undefined) {
    return `'${unknown}' is not a value of '${BRAND_GROUP_FIELD}'`
  }
  return membership(BRAND_GROUP_FIELD, filter.operator, filter.values)
}

// Python's `UUID(value)`, which the backend's `_resolved` decides with.
const isUuid = (value: string): boolean => {
  const hex = value
    .replace('urn:', '')
    .replace('uuid:', '')
    .replace(/^[{}]+|[{}]+$/g, '')
    .replaceAll('-', '')
  return /^[0-9a-f]{32}$/i.test(hex)
}

// A uuid the options no longer name (a deleted theme or dataset) is carried as
// `missing`, so the filter keeps matching nothing for it instead of
// broadening. A theme value that is not a uuid is a v1 hash's display name
// and is read as one. Mirrors the backend's `_resolved`.
function resolvedFilter(
  field: 'theme' | 'dataset',
  filter: SelectedFilterOption,
  byId: Record<string, string>,
): Condition[] | string {
  const names: string[] = []
  const missing: string[] = []
  for (const value of filter.values) {
    if (isNull(value)) {
      names.push(value)
      continue
    }
    const name =
      byId[value] ?? (field === 'theme' && !isUuid(value) ? value : undefined)
    if (name !== undefined) {
      if (!names.includes(name)) names.push(name)
    } else if (isUuid(value)) {
      if (!missing.includes(value)) missing.push(value)
    } else {
      return `${field} value '${value}' is not in the current filter options`
    }
  }
  return membership(field, filter.operator, names, undefined, missing)
}

// The legacy `Verified` / `Not Verified` convention on a boolean custom field.
function booleanFilter(
  filter: SelectedFilterOption,
  field: FieldSpec,
): Condition[] | string {
  const { plain, hasNull } = splitNulls(filter.values)
  const bools: boolean[] = []
  for (const value of plain) {
    const lowered = value.toLowerCase()
    const asBool =
      value === field.name || lowered === 'true'
        ? true
        : value === `Not ${field.name}` || lowered === 'false'
          ? false
          : null
    if (asBool === null) return `'${value}' is not a value of '${field.name}'`
    if (!bools.includes(asBool)) bools.push(asBool)
  }
  const equals: FieldCondition[] = bools.map((value, index) => ({
    field: field.name,
    op: 'eq',
    value,
    ...(hasNull && index === 0 ? { include_null: true } : {}),
  }))
  const positive: Condition | null =
    equals.length > 1
      ? { or: equals }
      : (equals[0] ?? (hasNull ? { field: field.name, op: 'is_null' } : null))
  if (positive === null) return []
  if (filter.operator === 'is') return [positive]
  if (filter.operator === 'is not') return [{ not: positive }]
  return `operator '${filter.operator}' on '${field.name}' has no YogiQL encoding`
}

function toConditions(
  filter: SelectedFilterOption,
  fields: FieldSpec[],
  names: YogiQLNames,
): Condition[] | string {
  const bucket = filter.groupBucket
  if (bucket) {
    if (!fields.some((f) => f.name === bucket.field)) {
      return `filter field '${bucket.field}' is not in this hub's catalog`
    }
    const closed = CLOSED_GROUP_VALUES[bucket.field]
    if (closed && !closed.has(bucket.value)) {
      return `'${bucket.value}' is not a value of '${bucket.field}'`
    }
    return [{ field: bucket.field, op: 'in', values: [bucket.value] }]
  }
  if (isNumericFilterField(filter.field)) {
    if (filter.numericValue == null) return []
    const op = NUMERIC_OP[filter.operator as NumericOperatorType]
    if (!op) {
      return `operator '${filter.operator}' on '${filter.field}' has no YogiQL encoding`
    }
    const threshold: Condition = {
      metric: NUMERIC_METRIC[filter.field]!,
      op,
      value: filter.numericValue,
    }
    return [filter.operator === 'not equal to' ? { not: threshold } : threshold]
  }
  switch (filter.field) {
    case 'sentiment':
    case 'post_sentiment':
      return sentimentFilter(filter)
    case 'score':
    case 'post_score':
      return ratingFilter(filter)
    case 'promotion':
      return promotionFilter(filter)
    case BRAND_GROUP_FIELD:
      return brandGroupFilter(filter, fields)
    case 'theme':
      return resolvedFilter('theme', filter, names.themes)
    case 'proj_uuid':
    case 'dataset':
      return resolvedFilter('dataset', filter, names.datasets)
    default:
  }
  const standard = STANDARD_FILTER_FIELD[filter.field]
  if (standard) return membership(standard, filter.operator, filter.values)
  const custom = fields.find((f) => f.custom && f.name === filter.field)
  if (!custom) return `filter field '${filter.field}' has no YogiQL name`
  return custom.shape === 'boolean'
    ? booleanFilter(filter, custom)
    : membership(custom.name, filter.operator, filter.values)
}

// YogiQL takes instants (07 §6): the stored second, verbatim, no millis.
const toInstant = (epochSeconds: number): string =>
  new Date(epochSeconds * 1000).toISOString().replace(/\.\d{3}Z$/, 'Z')

function dateCondition(dateRange: ChartPanel['dateRange']): Condition | null {
  // The legacy IR treats 0 as unbounded on that end.
  const start = dateRange.startDate ? toInstant(dateRange.startDate) : undefined
  const end = dateRange.endDate ? toInstant(dateRange.endDate) : undefined
  if (start && end) {
    return { field: 'date', op: 'between', values: [start, end] }
  }
  if (start) return { field: 'date', op: 'gte', value: start }
  if (end) return { field: 'date', op: 'lte', value: end }
  return null
}

export const SEARCH_OP: Record<string, Operator> = {
  'Includes all': 'contains_all',
  'Includes some': 'contains_any',
  'Excludes some': 'excludes_any',
  'Excludes all': 'excludes_all',
}

// Exactly one search mode: the smart-search phrase, else the keyword boxes
// (unknown keys dropped), else bare terms as the legacy AND join.
function searchConditions(panel: ChartPanel): Condition[] {
  const terms = panel.searchTerms.filter((t) => t && t.trim().length > 0)
  if (panel.smartSearchEnabled && terms.length) {
    return [{ field: 'text_search', op: 'smart_search', values: [terms[0]!] }]
  }
  const keyword: Condition[] = []
  for (const [box, values] of Object.entries(panel.searchCriteria ?? {})) {
    const op = SEARCH_OP[box]
    if (op && values.length) keyword.push({ field: 'text_search', op, values })
  }
  if (keyword.length || !terms.length) return keyword
  return [{ field: 'text_search', op: 'contains_all', values: terms }]
}

const uniqueByYogiQLMetric = (metrics: MetricType[]): MetricType[] =>
  metrics.filter(
    (m, i) =>
      metrics.findIndex(
        (o) => metricKey(toYogiQLMetric(o)) === metricKey(toYogiQLMetric(m)),
      ) === i,
  )

// The drawn metrics fill the spec's metric positions; whatever the panel and
// the weighting augmentation carry beyond them is appended, fetched but not
// drawn, bubble's companion counts last, in the order assemble_bubble appends them.
// A stored request keeps only the panel's own metrics (06 §30).
function placeMetrics(
  spec: YogiQLChartSpec,
  metrics: MetricType[],
  purpose: YogiQLPurpose,
): PlainMetric[] | string {
  // Rating and NPS are one YogiQL metric; a metric may appear once.
  const unique = uniqueByYogiQLMetric(metrics)
  const placed = placeSlots(spec.metrics, unique.length)
  const missing = placed[unique.length]
  if (missing) {
    return `${spec.name} needs metrics[${unique.length}] (${missing.role})`
  }
  if (purpose === 'store') return unique.map(toYogiQLMetric)
  const drawn = unique.slice(0, placed.length)
  const drawnKeys = drawn.map((m) => metricKey(toYogiQLMetric(m)))
  const extras = uniqueByYogiQLMetric(
    augmentMetricsForWeighting(metrics),
  ).filter((m) => !drawnKeys.includes(metricKey(toYogiQLMetric(m))))
  if (spec.name === 'bubble') {
    for (const count of [METRICS.RECORD_COUNT.id, METRICS.SENTENCE_COUNT.id]) {
      if (!drawn.includes(count) && !extras.includes(count)) extras.push(count)
    }
  }
  return [...drawn, ...extras].map(toYogiQLMetric)
}

function placeBreakdowns(
  spec: YogiQLChartSpec,
  panel: ChartPanel,
  fields: FieldSpec[],
): YogiQLBreakdown[] | string {
  const encoded: YogiQLBreakdown[] = []
  for (const breakdown of panel.breakdowns) {
    const yogiql = toYogiQLBreakdown(breakdown, fields)
    if (typeof yogiql === 'string') return yogiql
    encoded.push(yogiql)
    // A sankey draws a grouped breakdown as two columns, group then member.
    const member =
      'id' in breakdown ? STANDARD_BREAKDOWN[breakdown.id] : undefined
    if (spec.name === 'sankey' && GROUP_FIELDS.has(yogiql.field) && member) {
      encoded.push(member)
    }
  }
  // Legacy defaults a missing bucket to month server-side.
  if (spec.name === 'line') {
    encoded.push({ field: 'date', fn: panel.timeBucket ?? 'month' })
  }
  if (spec.name === 'ridgeline') encoded.push(ridgelineScore(panel).bins)
  const placed = placeSlots(spec.breakdowns, encoded.length)
  const missing = placed[encoded.length]
  if (missing) {
    return `${spec.name} needs breakdowns[${encoded.length}] (${missing.role})`
  }
  const allowed = [
    ...(PANEL_BREAKDOWN_ROLES[spec.name] ?? []),
    'Time axis',
    'Bins',
  ]
  const unplaced = encoded.length - placed.length
  const foreign = placed.find((slot) => !allowed.includes(slot.role))
  if (unplaced > 0 || foreign) {
    return `${spec.name} has no position for ${panel.breakdowns.length} panel breakdown(s)`
  }
  const breakdowns: YogiQLBreakdown[] = []
  panelBreakdownOrder(spec, encoded.length).forEach((position, k) => {
    breakdowns[position] = encoded[k]!
  })
  return breakdowns
}

/**
 * A panel's filters, date range and search as YogiQL conditions, or why one
 * has no encoding. The filter half of `toYogiQLChartRequest`. Pure.
 */
export function toYogiQLFilters(
  panel: ChartPanel,
  catalog: FieldCatalogResponse,
  names: YogiQLNames,
): Condition[] | string {
  const filters: Condition[] = []
  for (const filter of panel.filters) {
    const conditions = toConditions(filter, catalog.fields, names)
    if (typeof conditions === 'string') return conditions
    filters.push(...conditions)
  }
  const date = dateCondition(panel.dateRange)
  if (date) filters.push(date)
  filters.push(...searchConditions(panel))
  return filters
}

/** `query` is what the page posts; `store` is what a save persists: the
 *  panel's own metrics only. */
export type YogiQLPurpose = 'query' | 'store'

/**
 * Serialise a charts-v2 panel into a YogiQL chart request, or say why it
 * cannot be (the caller then takes the legacy route). Pure.
 */
export function toYogiQLChartRequest(
  panel: ChartPanel,
  catalog: FieldCatalogResponse,
  names: YogiQLNames,
  purpose: YogiQLPurpose = 'query',
): YogiQLEncoding {
  const fail = (reason: string): YogiQLEncoding => ({ ok: false, reason })
  const chart = CHART_BY_TYPE[panel.chartType]
  if (!chart) return fail(`chart type '${panel.chartType}' has no YogiQL spec`)
  const spec = YOGIQL_CHART_SPECS[chart]

  const metrics =
    chart === 'ridgeline'
      ? ridgelineScore(panel).metrics
      : chart === 'sankey'
        ? sankeyMetrics(panel)
        : placeMetrics(spec, panel.metrics, purpose)
  if (typeof metrics === 'string') return fail(metrics)
  const breakdowns = placeBreakdowns(spec, panel, catalog.fields)
  if (typeof breakdowns === 'string') return fail(breakdowns)

  const filters = toYogiQLFilters(panel, catalog, names)
  if (typeof filters === 'string') return fail(filters)
  if (chart === 'ridgeline') filters.push(...ridgelineScore(panel).filters)

  return {
    ok: true,
    request: withYogiQLVersion({
      chart_type: chart,
      metrics,
      ...(breakdowns.length ? { breakdowns } : {}),
      filters,
      limit: YOGIQL_ROW_CEILING,
    }),
  }
}

export type { Scalar }
